// Dependencies
import React from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

// Material UI Components
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

// Components
import Iconify from 'src/components/Iconify';
import iconifyIcons from 'src/assets/iconifyIcons';

// Types
type Props = {
  path: string;
  alternativePaths?: string[];
  title: string;
  additional?: React.ReactNode;
  icon: typeof iconifyIcons[keyof typeof iconifyIcons];
  onClick?: () => void;
};

// ----------------------------------------------------------------------
export default function NavListItem({
  path,
  title,
  additional,
  icon,
  onClick,
  alternativePaths,
}: Props) {
  // Get path name
  const { pathname, asPath } = useRouter();

  // Check if the current path is active
  const isActive =
    (!path.startsWith('#') && pathname.endsWith(path)) ||
    (!path.startsWith('#') && asPath.endsWith(path)) ||
    (alternativePaths || [])?.some(
      (alternativePath) =>
        (!alternativePath.startsWith('#') && pathname.endsWith(alternativePath)) ||
        (!alternativePath.startsWith('#') && asPath.endsWith(alternativePath))
    );

  return (
    <NextLink href={path} passHref>
      <ListItemButton
        disabled={isActive}
        onClick={onClick}
        sx={(theme) => ({
          ...theme.typography.body2,
          position: 'relative',
          height: 48,
          textTransform: 'capitalize',
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(1.5),
          marginBottom: theme.spacing(0.5),
          color: theme.palette.text.secondary,
          borderRadius: theme.shape.borderRadius / 8,
          ...(isActive
            ? {
                ...theme.typography.subtitle2,
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  theme.palette.action.selectedOpacity
                ),
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: '100%',
                  borderRadius: '8px 0 0 8px',
                  backgroundColor: theme.palette.primary.main,
                },
              }
            : {}),
          '&.Mui-disabled': {
            opacity: 'unset',
          },
        })}
      >
        <ListItemIcon
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            '& svg': { width: '100%', height: '100%' },
          }}
        >
          <Iconify icon={icon} />
        </ListItemIcon>
        <ListItemText
          sx={{
            whiteSpace: 'nowrap',
            transition: (theme) =>
              theme.transitions.create(['width', 'opacity'], {
                duration: theme.transitions.duration.shorter,
              }),
          }}
          disableTypography
          primary={
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <span>{title}</span>
              {!!additional && additional}
            </Box>
          }
        />
      </ListItemButton>
    </NextLink>
  );
}
